import { differenceInMonths, format } from 'date-fns'

import { Game } from '../../../../features/game/types/Game'
import { AnalyzedFilter, allMarketsIso } from '../types/InternalGame'

export const minGameSearchTermLength = 2
export const gameSearchDebounceMs = 500
export const outdatedAnalysisMonths = 6

const legacyListSearchFields = [
  'name',
  'artist',
  'gpsPerMarket',
  'icon',
  'icons',
  'stage',
  'internal',
  'appId',
  'stores',
  'markets',
  'lastAnalysisAt',
  'latestAnalysis',
  'reviewId',
  'reviewPublished',
  'conventionalSubgenre',
]

export type GameListSearchBody = { term: string; include: string; marketIso?: string }

export const getGameListSearchBody = (term: string, marketIso: string): GameListSearchBody => ({
  term,
  include: legacyListSearchFields.join(','),
  ...(marketIso === allMarketsIso ? {} : { marketIso }),
})

export const getCellMarketIso = (marketIso: string) => (marketIso === allMarketsIso ? 'us' : marketIso)

const hasAnalysis = (game: Game, marketIso: string) => {
  const latest = (game.latestAnalysis ?? {}) as Record<string, unknown>
  return marketIso === allMarketsIso ? Object.keys(latest).length > 0 : !!latest[marketIso]
}

export const filterListGames = (games: Game[], marketIso: string, analyzed: AnalyzedFilter) =>
  games.filter((game) => {
    if (analyzed === AnalyzedFilter.Analyzed && !hasAnalysis(game, marketIso)) return false
    if (analyzed === AnalyzedFilter.NotAnalyzed && hasAnalysis(game, marketIso)) return false
    return marketIso === allMarketsIso || (game.markets ?? []).some((market) => market.iso === marketIso)
  })

export const getRoundedPowerscore = (game: Game, marketIso: string) => {
  const powerscore = game.gpsPerMarket?.[marketIso]
  return powerscore ? Math.round(powerscore) : undefined
}

export const getLastAnalysisAt = (game: Game, marketIso: string): number | undefined => game.lastAnalysisAt?.[marketIso] || undefined

export const formatLastAnalysisDate = (timestamp: number) => format(timestamp, 'dd.MM.yyyy')

export const isLastAnalysisOlderThan = (timestamp: number, months: number, now = new Date()) => differenceInMonths(now, timestamp) >= months

export const getMarketsSummary = (game: Game) => ({
  stores: (game.stores ?? []).map((store) => store.name),
  markets: (game.markets ?? []).map((market) => market.name),
})

export const togglePinnedGameId = (pinnedIds: string[], gameId: string) =>
  pinnedIds.includes(gameId) ? pinnedIds.filter((id) => id !== gameId) : [...pinnedIds, gameId]
